import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  barrattAPI,
  type SupplierUploadTracking,
  type UploadPeriod,
  type UploadSummary,
} from '../../../lib/api/barratt';
import { useFetchData, getErrorMessage } from '../../../hooks/useFetchData';

export type UploadTab = 'uploaded' | 'not-uploaded';

export interface UploadHistoryBundle {
  summary: UploadSummary;
  uploaded: SupplierUploadTracking[];
  notUploaded: SupplierUploadTracking[];
}

export interface UseUploadHistoryResult {
  periods: UploadPeriod[];
  /** Selected 'YYYY-MM'; empty until the period list has loaded. */
  period: string;
  setPeriod: (period: string) => void;
  summary: UploadSummary | null;
  uploaded: SupplierUploadTracking[];
  notUploaded: SupplierUploadTracking[];
  loading: boolean;
  error: string | null;
  retry: () => void;
  /** Email one row's supplier. */
  chase: (row: SupplierUploadTracking) => Promise<void>;
  /** Email every not-uploaded row in the period, or just the given rows. */
  chaseAll: (rows?: SupplierUploadTracking[]) => Promise<void>;
  chasingIds: number[];
  chasingAll: boolean;
}

const NO_PERIODS = 'No upload periods are configured yet.';

/**
 * Loads the period list, then the summary + both supplier lists for the
 * selected period, and exposes the chase actions.
 *
 * Calls: GET supplier-uploads/periods/, then for the period
 * GET supplier-uploads/summary/, /uploaded/, /not_uploaded/ (all ?period=);
 * POST supplier-uploads/chase/ with {ids} or {period}.
 *
 * @returns Period selection, the loaded rows, loading/error state and chase handlers
 */
export function useUploadHistory(): UseUploadHistoryResult {
  const [period, setPeriod] = useState('');
  const [chasingIds, setChasingIds] = useState<number[]>([]);
  const [chasingAll, setChasingAll] = useState(false);

  const periodsQuery = useFetchData<UploadPeriod[]>(async () => {
    const { data } = await barrattAPI.getUploadPeriods();
    return data.periods ?? [];
  });

  // Default to the current period once the list arrives; the user's choice wins after that.
  useEffect(() => {
    if (period || !periodsQuery.data) return;
    const current = periodsQuery.data.find((p) => p.is_current) ?? periodsQuery.data[0];
    if (current) setPeriod(current.period);
  }, [period, periodsQuery.data]);

  const bundleQuery = useFetchData<UploadHistoryBundle | null>(async () => {
    if (!period) return null;
    const [summaryRes, uploadedRes, notUploadedRes] = await Promise.all([
      barrattAPI.getUploadSummary(period),
      barrattAPI.getSuppliersUploaded(period),
      barrattAPI.getSuppliersNotUploaded(period),
    ]);
    return {
      summary: summaryRes.data,
      uploaded: uploadedRes.data ?? [],
      notUploaded: notUploadedRes.data ?? [],
    };
  }, { deps: [period] });

  // Both refetch functions are stable (useCallback with no deps inside useFetchData).
  const { refetch: refetchPeriods, error: periodsError } = periodsQuery;
  const { refetch: refetchBundle } = bundleQuery;

  const noPeriods = periodsQuery.data !== null && periodsQuery.data.length === 0;
  const waitingForPeriod = !period && !periodsError && !noPeriods;
  const loading = periodsQuery.loading || bundleQuery.loading || waitingForPeriod;
  const error = periodsError ?? (noPeriods ? NO_PERIODS : null) ?? bundleQuery.error;

  const retry = useCallback(() => {
    if (periodsError || noPeriods) {
      void refetchPeriods();
    } else {
      void refetchBundle();
    }
  }, [periodsError, refetchPeriods, noPeriods, refetchBundle]);

  const chase = useCallback(async (row: SupplierUploadTracking) => {
    setChasingIds((ids) => [...ids, row.id]);
    try {
      const { data } = await barrattAPI.chaseSupplierUploads({ ids: [row.id] });
      if (data.sent > 0) {
        toast.success(data.message || `Reminder sent to ${row.supplier_name ?? 'supplier'}`);
      } else {
        toast.warning(data.message || `No reminder sent — ${row.supplier_name ?? 'supplier'} has no contact email.`);
      }
      await refetchBundle();
    } catch (err: unknown) {
      console.error('Failed to send reminder:', err);
      toast.error(getErrorMessage(err, 'Failed to send reminder'));
    } finally {
      setChasingIds((ids) => ids.filter((id) => id !== row.id));
    }
  }, [refetchBundle]);

  const chaseAll = useCallback(async (rows?: SupplierUploadTracking[]) => {
    if (!period) return;
    setChasingAll(true);
    try {
      const payload = rows ? { ids: rows.map((r) => r.id) } : { period };
      const { data } = await barrattAPI.chaseSupplierUploads(payload);
      const skipped = data.skipped > 0 ? ` (${data.skipped} skipped)` : '';
      if (data.sent > 0) {
        toast.success(data.message || `${data.sent} reminder${data.sent === 1 ? '' : 's'} sent${skipped}`);
      } else {
        toast.warning(data.message || `No reminders sent${skipped}`);
      }
      await refetchBundle();
    } catch (err: unknown) {
      console.error('Failed to chase suppliers:', err);
      toast.error(getErrorMessage(err, 'Failed to send reminders'));
    } finally {
      setChasingAll(false);
    }
  }, [period, refetchBundle]);

  return {
    periods: periodsQuery.data ?? [],
    period,
    setPeriod,
    summary: bundleQuery.data?.summary ?? null,
    uploaded: bundleQuery.data?.uploaded ?? [],
    notUploaded: bundleQuery.data?.notUploaded ?? [],
    loading,
    error,
    retry,
    chase,
    chaseAll,
    chasingIds,
    chasingAll,
  };
}
